<template>
  <div class="border-bdr-default mb-3 w-full overflow-hidden rounded-lg border">
    <div class="bg-bgr-active flex h-9 w-full cursor-pointer items-center gap-2 px-3" @click="open = !open">
      <Icon src="fill/warning" class="text-icon-warning" />
      <p class="text-body-default-m truncate">{{ t("order_lines.dangerous_goods_item") }}</p>
      <Icon
        src="caret_down"
        class="text-icon-warning ml-auto transition duration-400"
        :class="open ? '-rotate-180 transform' : ''"
      />
    </div>
    <div class="grid overflow-hidden p-3 transition-all duration-400" :class="open ? 'grid-rows-1fr' : 'grid-rows-0'">
      <div class="overflow-hidden">
        <Dropdown
          v-model="selectedTemplate"
          :readonly="dGTemplatesComputed.length == 2"
          :label="t('dangerous_goods_template')"
          :placeholder="t('select_dangerous_template_placeholder')"
          :message="t('dangerous_goods_helper_text')"
          :options="dGTemplatesComputed"
        />
        <Divider class="mt-4! mb-3!" />
        <p class="text-body-default-m py-1">{{ t("mark_as") }}</p>
        <div class="flex gap-1.5">
          <SelectRadioButton
            v-model="radioValue"
            :title="t('LQ')"
            :subtitle="t('limited_quantity')"
            value="limited_quantity"
            class="w-full"
          />
          <SelectRadioButton
            v-model="radioValue"
            :title="t('DG')"
            :subtitle="t('full_declaration')"
            value="none"
            class="w-full"
          />
        </div>
        <Divider class="my-4!" />
        <div class="mb-1.5 grid grid-cols-3 gap-3">
          <InputNumber
            v-model="dangerousGoods.packaging_quantity"
            is-required
            class="col-span-1"
            :label="t('order_lines.packaging_quantity')"
            :readonly="disabled"
            :error="errors.packaging_quantity"
            @blur="
              () =>
                validateField(
                  'packaging_quantity',
                  dangerousGoods.packaging_quantity,
                  t('order_lines.packaging_quantity')
                )
            "
          />
          <InputText
            v-model="dangerousGoods.packaging_description"
            is-required
            class="col-span-2"
            :label="t('order_lines.package_type')"
            :readonly="disabled"
            :error="errors.packaging_description"
            @blur="
              () =>
                validateField(
                  'packaging_description',
                  dangerousGoods.packaging_description,
                  t('order_lines.package_type')
                )
            "
          />
        </div>
        <div class="grid grid-cols-6 gap-3">
          <InputNumber
            v-model="dangerousGoods.un_number"
            :label="t('order_lines.un_number')"
            class="col-span-2"
            :readonly="disabled"
            :error="errors.un_number"
            is-required
            @blur="() => validateField('un_number', dangerousGoods.un_number, t('order_lines.un_number'))"
          />
          <InputText
            v-model="dangerousGoods.proper_shipping_name"
            :label="t('order_lines.official_goods_designation')"
            class="col-span-4"
            :readonly="disabled"
            :error="errors.proper_shipping_name"
            is-required
            @blur="
              () =>
                validateField(
                  'proper_shipping_name',
                  dangerousGoods.proper_shipping_name,
                  t('order_lines.official_goods_designation')
                )
            "
          />
          <div class="col-span-6">
            <Dropdown
              v-model="dangerousGoods.packing_instruction"
              :label="t('order_lines.packing_instructions')"
              :options="packing_instruction_options"
              :error="errors.packing_instruction"
            />
          </div>
          <InputText
            v-if="radioValue !== 'limited_quantity'"
            v-model="dangerousGoods.nos_description"
            :label="t('order_lines.nos_designation')"
            class="col-span-6"
            :readonly="disabled"
            :error="errors.nos_description"
            is-required
            @blur="
              () => validateField('nos_description', dangerousGoods.nos_description, t('order_lines.nos_designation'))
            "
          />
          <InputText
            v-model="hazard_class"
            :label="t('order_lines.hazard_class')"
            :class="radioValue !== 'limited_quantity' ? 'col-span-2' : 'col-span-6'"
            :readonly="disabled"
            :error="errors.hazard_class"
            is-required
            @blur="() => validateField('hazard_class', hazard_class, t('order_lines.hazard_class'))"
          />
          <div v-if="radioValue == 'limited_quantity'" class="col-span-6">
            <Dropdown
              v-model="dangerousGoods.packing_group"
              is-required
              :label="t('order_lines.package_group')"
              :options="package_group_options"
              :readonly="disabled"
              :error="errors.packing_group"
            />
          </div>
          <InputText
            v-if="radioValue !== 'limited_quantity'"
            v-model="hazard_class2"
            :label="`${t('order_lines.hazard_class')} (||)`"
            class="col-span-2"
            :readonly="disabled"
            :error="errors.hazard_class2"
          />
          <InputText
            v-if="radioValue !== 'limited_quantity'"
            v-model="hazard_class2plus"
            :label="`${t('order_lines.hazard_class')} (||)`"
            class="col-span-2"
            :readonly="disabled"
            :error="errors.hazard_class2plus"
          />
          <div v-if="radioValue !== 'limited_quantity'" class="col-span-3">
            <Dropdown
              v-model="dangerousGoods.packing_group"
              :label="t('order_lines.package_group')"
              :options="package_group_options"
              :readonly="disabled"
              :error="errors.packing_group"
            />
          </div>
          <div v-if="radioValue !== 'limited_quantity'" class="col-span-3">
            <Dropdown
              v-model="dangerousGoods.tunnel_restriction_code"
              :label="t('order_lines.tunnel_restriction_code')"
              :options="tunnel_restriction_options"
              :readonly="disabled"
              :error="errors.tunnel_restriction_code"
            />
          </div>
          <div class="col-span-6 mb-1.5">
            <InputUnit
              v-model:unit="dangerousGoods.quantity_unit"
              v-model:value="dangerousGoods.quantity_value"
              :label="t('order_lines.quantity_unit')"
              type="number"
              :options="weight_units"
              :readonly="disabled"
              is-required
              :step="0.01"
              :unit-filterable="false"
              @blur="
                () => validateField('quantity_value', dangerousGoods.quantity_value, t('order_lines.quantity_unit'))
              "
            />
            <Message v-if="errors.quantity_value" variant="invalid">
              {{ errors.quantity_value }}
            </Message>
          </div>
          <div
            class="bg-bgr-secondary border-bdr-default col-span-full mt-1 flex cursor-pointer flex-col items-start gap-1 rounded-xl border px-3 py-2"
            @click="dangerousGoods.environmentally_hazardous = !dangerousGoods.environmentally_hazardous"
          >
            <div class="flex items-center gap-3">
              <Toggle v-model="dangerousGoods.environmentally_hazardous" @click.stop="" />
              <span class="font-medium">{{ t("order_lines.envioment_header") }}</span
              ><br />
            </div>
            <p class="text-body-default pl-10">
              <span class="text-fgr-secondary">{{ t("order_lines.envioment_obs") }}</span>
            </p>
          </div>
        </div>
        <div class="bg-bgr-active mt-4 w-full rounded-lg p-2">
          <p class="text-fgr-hint text-body-default">{{ t("preview") }}</p>
          <p class="text-body-default truncate">
            {{ radioValue == "limited_quantity" ? t("preview_limited_goods") : dangerousGoodsSummary }}
          </p>
        </div>
        <div class="col-span-full mt-4 flex justify-end">
          <Button
            :disabled="disabled"
            :variant="ButtonVariant.GhostDestructive"
            type="button"
            @click="emit('deleteLine')"
            >{{ t("delete") }} {{ t("order_lines.dangerous_goods_item").toLowerCase() }}</Button
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import type { DangerousGoods } from "~/types/orderline";

  defineProps<{
    disabled?: boolean;
  }>();

  const dangerousGoods = defineModel<DangerousGoods>({
    required: true,
  });

  const { t } = useI18n();
  const { dangerousGoodsTemplates, formatDangerousGoodsSummary } = useOrderlines();
  const emit = defineEmits<{
    (e: "deleteLine"): void;
  }>();

  // Validation errors
  const errors = reactive({
    packaging_quantity: "",
    packaging_description: "",
    un_number: "",
    proper_shipping_name: "",
    nos_description: "",
    packing_instruction: "",
    hazard_class: "",
    hazard_class2: "",
    hazard_class2plus: "",
    packing_group: "",
    tunnel_restriction_code: "",
    quantity_value: "",
    quantity_unit: "",
  });

  const open = ref(true);
  const selectedTemplate = ref(null);
  const isApplyingTemplate = ref(false);

  // Validation function
  const validateField = (field: keyof typeof errors, value: unknown, label: string) => {
    if (!value || value === "" || value === null || value === undefined) {
      errors[field] = t("is_required_field", { entity: label });
    } else {
      errors[field] = "";
    }
  };

  const dangerousGoodsSummary = computed(() => {
    return formatDangerousGoodsSummary(dangerousGoods.value);
  });

  const dGTemplatesComputed = computed(() => {
    return [
      { label: t("create_dg_templates"), value: "template_text_helper", disabled: true },
      null,
      ...dangerousGoodsTemplates.value,
    ];
  });

  const package_group_options = [
    { label: "-", value: "" },
    { label: "I", value: "I" },
    { label: "II", value: "II" },
    { label: "III", value: "III" },
  ];

  const packing_instruction_options = [
    { label: "PI901", value: "PI901" },
    { label: "PI650", value: "PI650" },
    { label: "PI910", value: "PI910" },
    { label: "PI911", value: "PI911" },
    { label: "PI965", value: "PI965" },
    { label: "PI966", value: "PI966" },
    { label: "PI967", value: "PI967" },
    { label: "PI968", value: "PI968" },
    { label: "PI969", value: "PI969" },
    { label: "PI970", value: "PI970" },
    { label: "PIA01", value: "PIA01" },
  ];

  const tunnel_restriction_options = [
    { label: "-", value: "" },
    { label: "(B1000C)", value: "(B1000C)" },
    { label: "(B/D)", value: "(B/D)" },
    { label: "(B/E)", value: "(B/E)" },
    { label: "(C)", value: "(C)" },
    { label: "(C5000D)", value: "(C5000D)" },
    { label: "(C/D)", value: "(C/D)" },
    { label: "(C/E)", value: "(C/E)" },
    { label: "(D)", value: "(D)" },
    { label: "(D/E)", value: "(D/E)" },
    { label: "(E)", value: "(E)" },
  ];

  const weight_units = [
    { label: "kg", value: "KG" },
    { label: "l", value: "L" },
  ];

  const radioValue = ref("limited_quantity");

  watch(radioValue, (value) => {
    dangerousGoods.value.limited_quantity = value === "limited_quantity";
    dangerousGoods.value.environmentally_hazardous = value === "environmentally_hazardous";
  });

  watch(
    () => selectedTemplate.value,
    (newValue) => {
      if (newValue !== null) {
        const template = dangerousGoodsTemplates.value.find((t) => t.value === newValue);
        if (template) {
          isApplyingTemplate.value = true;
          Object.assign(dangerousGoods.value, template);
          if (dangerousGoods.value.limited_quantity) {
            radioValue.value = "limited_quantity";
          } else if (dangerousGoods.value.environmentally_hazardous) {
            radioValue.value = "environmentally_hazardous";
          } else {
            radioValue.value = "none";
          }
          hazard_class.value = dangerousGoods.value.hazard_class || "";
          nextTick(() => {
            isApplyingTemplate.value = false;
            validateField(
              "packaging_quantity",
              dangerousGoods.value.packaging_quantity,
              t("order_lines.packaging_quantity")
            );
            validateField(
              "packaging_description",
              dangerousGoods.value.packaging_description,
              t("order_lines.package_type")
            );
            validateField("un_number", dangerousGoods.value.un_number, t("order_lines.un_number"));
            validateField(
              "proper_shipping_name",
              dangerousGoods.value.proper_shipping_name,
              t("order_lines.official_goods_designation")
            );
            validateField("nos_description", dangerousGoods.value.nos_description, t("order_lines.nos_designation"));
            validateField("hazard_class", hazard_class.value, t("order_lines.hazard_class"));
            validateField("quantity_value", dangerousGoods.value.quantity_value, t("order_lines.quantity_unit"));
          });
        }
      }
    }
  );

  watch(
    () => ({
      packaging_description: dangerousGoods.value.packaging_description,
      un_number: dangerousGoods.value.un_number,
      proper_shipping_name: dangerousGoods.value.proper_shipping_name,
      packing_instruction: dangerousGoods.value.packing_instruction,
      nos_description: dangerousGoods.value.nos_description,
      hazard_class: dangerousGoods.value.hazard_class,
      packing_group: dangerousGoods.value.packing_group,
      tunnel_restriction_code: dangerousGoods.value.tunnel_restriction_code,
      quantity_unit: dangerousGoods.value.quantity_unit,
      limited_quantity: dangerousGoods.value.limited_quantity,
      environmentally_hazardous: dangerousGoods.value.environmentally_hazardous,
    }),
    () => {
      if (!isApplyingTemplate.value && selectedTemplate.value !== null) {
        selectedTemplate.value = null;
      }
    }
  );

  const hazard_class = ref("");
  const hazard_class2 = ref("");
  const hazard_class2plus = ref("");

  watch([hazard_class, hazard_class2, hazard_class2plus], () => {
    const parts = [hazard_class.value];
    if (hazard_class2.value) {
      parts.push(`(${hazard_class2.value}${hazard_class2plus.value ? `+${hazard_class2plus.value}` : ""})`);
    }
    dangerousGoods.value.hazard_class = parts.join("");
  });

  if (dangerousGoods.value.hazard_class) {
    const match = dangerousGoods.value.hazard_class.match(/^([^\(]+)(\(([^+]+)(\+([^+]+))?\))?$/);
    if (match) {
      hazard_class.value = match[1];
      hazard_class2.value = match[3] || "";
      hazard_class2plus.value = match[5] || "";
      if (dangerousGoods.value.limited_quantity) {
        radioValue.value = "limited_quantity";
      } else if (dangerousGoods.value.environmentally_hazardous) {
        radioValue.value = "environmentally_hazardous";
      } else {
        radioValue.value = "none";
      }
    }
  }
</script>
